import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { FaFilter, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import ProductCard from "../../components/ProductCard";
import { useCart } from "../../hooks/useCart";
import { useProducts } from "../../hooks/useProducts";
import { useToastContext } from "../../context/ToastContext";
import categories from "../../data/categories";
import styles from "./ProductsPage.module.css";

const SORT_OPTIONS = [
  { value: "newest",     label: "最新" },
  { value: "price_asc",  label: "價格：低到高" },
  { value: "price_desc", label: "價格：高到低" },
  { value: "rating",     label: "評分最高" },
];

function ProductsPage() {
  const [searchParams] = useSearchParams();
  const { addItem } = useCart();
  const { addToast } = useToastContext();

  const {
    products,
    allFiltered,
    totalPages,
    page,
    setPage,
    search, setSearch,
    categories: selectedCats, setCategories,
    priceMin, setPriceMin,
    priceMax, setPriceMax,
    onlySale, setOnlySale,
    onlyInStock, setOnlyInStock,
    sortBy, setSortBy,
  } = useProducts();

  // Sync URL params on mount
  useEffect(() => {
    const cat = searchParams.get("category");
    const q = searchParams.get("search");
    const sale = searchParams.get("onlySale");
    if (cat) setCategories([cat]);
    if (q) setSearch(q);
    if (sale === "true") setOnlySale(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleAddToCart(product) {
    addItem(product);
    addToast(`「${product.name}」已加入購物車`, "success");
  }

  function toggleCategory(catId) {
    if (selectedCats.includes(catId)) {
      setCategories(selectedCats.filter(c => c !== catId));
    } else {
      setCategories([...selectedCats, catId]);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Sidebar Filters */}
        <aside className={styles.sidebar}>
          <h3 className={styles.filterTitle}>
            <FaFilter /> 篩選
          </h3>

          <div className={styles.filterGroup}>
            <h4 className={styles.filterLabel}>分類</h4>
            {categories.map(cat => (
              <label key={cat.id} className={styles.checkLabel}>
                <input
                  type="checkbox"
                  checked={selectedCats.includes(cat.id)}
                  onChange={() => toggleCategory(cat.id)}
                />
                {cat.name}
              </label>
            ))}
          </div>

          <div className={styles.filterGroup}>
            <h4 className={styles.filterLabel}>價格範圍</h4>
            <div className={styles.priceRange}>
              <input
                type="number"
                placeholder="最低"
                value={priceMin}
                onChange={e => setPriceMin(e.target.value)}
                className={styles.priceInput}
                min="0"
              />
              <span>–</span>
              <input
                type="number"
                placeholder="最高"
                value={priceMax}
                onChange={e => setPriceMax(e.target.value)}
                className={styles.priceInput}
                min="0"
              />
            </div>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.checkLabel}>
              <input
                type="checkbox"
                checked={onlySale}
                onChange={e => setOnlySale(e.target.checked)}
              />
              只看特價商品
            </label>
            <label className={styles.checkLabel}>
              <input
                type="checkbox"
                checked={onlyInStock}
                onChange={e => setOnlyInStock(e.target.checked)}
              />
              只看有庫存
            </label>
          </div>
        </aside>

        {/* Main Content */}
        <div className={styles.main}>
          <div className={styles.toolbar}>
            <p className={styles.resultCount}>
              共 <strong>{allFiltered.length}</strong> 件商品
            </p>

            <div className={styles.toolbarRight}>
              <input
                type="search"
                placeholder="搜尋商品..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className={styles.searchInput}
                aria-label="搜尋商品"
              />
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className={styles.sortSelect}
                aria-label="排序方式"
              >
                {SORT_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {products.length === 0 ? (
            <div className={styles.empty}>
              <p>沒有符合條件的商品</p>
              <button onClick={() => { setCategories([]); setSearch(""); setOnlySale(false); setOnlyInStock(false); setPriceMin(""); setPriceMax(""); }} className={styles.resetBtn}>
                清除篩選
              </button>
            </div>
          ) : (
            <div className={styles.grid}>
              {products.map(product => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onAddToCart={handleAddToCart}
                />
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className={styles.pageBtn}
                aria-label="上一頁"
              >
                <FaChevronLeft />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`${styles.pageBtn} ${p === page ? styles.pageBtnActive : ""}`}
                  aria-label={`第 ${p} 頁`}
                  aria-current={p === page ? "page" : undefined}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                className={styles.pageBtn}
                aria-label="下一頁"
              >
                <FaChevronRight />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProductsPage;
